/**
 * Module 'trim': trimming a run of characters off the end (or both ends) of a string, in
 * linear time. V8 runs a trailing-run regex such as /[. ]+$/, /\/+$/ or /^-+|-+$/g in time
 * quadratic in a long run of those characters that does not reach the end: it tries the run
 * from every position in it. 40,000 dots and then a letter take about a second, so any text a
 * person, a model or another machine sends is trimmed with these loops instead (containment
 * review F3, 18 Oct 2026). Browser-safe.
 */

/** The characters to trim: a string listing them, or a test for one UTF-16 code unit. */
export type TrimChars = string | ((ch: string) => boolean);

const testOf = (chars: TrimChars): ((ch: string) => boolean) => (typeof chars === 'string' ? (ch) => chars.includes(ch) : chars);

/** Is this one character JavaScript's \s (every \s character is a single UTF-16 code unit)? */
export const isRegexSpace = (ch: string): boolean => /^\s$/.test(ch);

/** s without the given characters at its end: s.replace(/[chars]+$/, ''), in linear time. */
export function trimEndOf(s: string, chars: TrimChars): string {
  const t = testOf(chars);
  let end = s.length;
  while (end > 0 && t(s[end - 1]!)) end--;
  return end === s.length ? s : s.slice(0, end);
}

/** s without the given characters at its start: s.replace(/^[chars]+/, ''). */
export function trimStartOf(s: string, chars: TrimChars): string {
  const t = testOf(chars);
  let start = 0;
  while (start < s.length && t(s[start]!)) start++;
  return start === 0 ? s : s.slice(start);
}

/** s without the given characters at either end: s.replace(/^[chars]+|[chars]+$/g, ''). */
export function trimBothOf(s: string, chars: TrimChars): string {
  return trimEndOf(trimStartOf(s, chars), chars);
}
